import { AfterViewInit, Component, OnInit } from '@angular/core';
import { TipAssistantComponent } from '../components/tip-assistant/tip-assistant.component';
import { LucideAngularModule } from 'lucide-angular';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { Icons } from '../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { NgxPaginationModule } from 'ngx-pagination';
import { UserDataService } from '../../shared/services/auth/userData.service';
import { TipsService } from '../../shared/services/tips.service';
import { Tip } from '../../shared/models/tip';
import { HistoryComponent } from '../components/history/history.component';
import { ManageTipComponent } from '../components/manage-tip/manage-tip.component';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';
import { CreateAssessCaseComponent } from '../components/create-assess-case/create-assess-case.component';
import { NsideAssessService } from '../../shared/services/assess.service';
import { IAssessCaseCreate, IStudentCreate, IStudentInfo } from '../../shared/models/assess';
import { StudentInfoFormComponent } from '../components/student-info-form/student-info-form.component';
import { StudentInfoReviewComponent } from '../components/student-info-review/student-info-review.component';
import { StudentCasesComponent } from '../components/student-cases/student-cases.component';
declare var initFlowbite: any;

@Component({
  selector: 'dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TipAssistantComponent,
    LucideAngularModule,
    PopupMessageComponent,
    SpinnerComponent,
    NgxPaginationModule,
    HistoryComponent,
    ManageTipComponent,
    CreateAssessCaseComponent,
    StudentInfoFormComponent,
    StudentInfoReviewComponent,
    StudentCasesComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit, AfterViewInit {
  icons = Icons;

  buildingId: string | null = null;
  hasAssess: string | null = null;
  userAssessPermission: string | null = null;
  regionId: string | null = null;
  publicTipUrl = environment.apiBaseUrl;
  schoolTipUrl = environment.apiBaseUrl;
  searchQuery: string = '';
  statusFilter: string = '';
  tipsList: Tip[] = [];
  filteredTipsList: Tip[] = [];
  currentTip: Tip | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  page = 1;
  itemsPerPage = 10;
  studentSSID: string = '';
  currentStudent: IStudentInfo | null = null;

  loading: boolean = false;
  showHistory: boolean = false;
  showManage: boolean = false;
  showCreateAssess: boolean = false;
  showStudentSSIDForm: boolean = false;
  showStudentInfoForm: boolean = false;
  showStudentInfoReview: boolean = false;
  showStudentCases: boolean = false;

  expandedTipIndex: number | null = null;

  constructor(
    private userDataService: UserDataService,
    private tipsService: TipsService,
    private nSideAssessService: NsideAssessService
  ) { }

  ngOnInit(): void {
    this.buildingId = this.userDataService.getBuildingId();
    this.hasAssess = this.userDataService.getHasAssess();
    this.userAssessPermission = this.userDataService.getUserAssessPermission();
    this.regionId = this.userDataService.getRegionId();
    console.log('Building ID from Service:', this.buildingId);
    
    if (localStorage.getItem("tip_building_id")) { // get building Id from local storage
      this.buildingId = localStorage.getItem("tip_building_id");
      this.userDataService.setBuildingId(this.buildingId + ""); // set building Id from local storage
    }

    this.schoolTipUrl = environment.apiBaseUrl + '/building/' + this.buildingId;
    this.loadAllTips();
  }
  
  ngAfterViewInit(): void {
    setTimeout(() => initFlowbite(), 100);
  }

  onStatusFilterChange(status: string): void {
    this.statusFilter = status;
    this.filterTipsList();
  }

  get statusFilterLabel(): string {
    switch (this.statusFilter) {
      case '0': return 'Open';
      case '1': return 'In Review';
      case '2': return 'Closed';
      default: return 'All Statuses';
    }
  }

  filterTipsList(): void {
    this.filteredTipsList = this.tipsList.filter((tip: Tip) => {
      const matchesSearch =
        tip.email?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        tip.full_name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        tip.tip_summary?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        tip.phone?.toString().includes(this.searchQuery);
  
      const matchesStatus =
        this.statusFilter === '' || tip.status?.toString() === this.statusFilter;
  
      return matchesSearch && matchesStatus;
    });
  }
  

  private loadAllTips(): void {
    if (this.buildingId) {
      this.loading = true;
      this.tipsService.getAllTipsByBuildingId(this.buildingId).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Building Tips:', response);
          if (!response.has_error) {
            this.tipsList = response.payload.map((tip: Tip) => ({
              ...tip,
              submission_date: tip.submission_date ? new Date(tip.submission_date) : null
            }));
            this.filteredTipsList = this.tipsList.length > 0 ? this.tipsList.reverse() : [];
            setTimeout(() => {
              initFlowbite();
            }, 100);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching building tips list:', error);
          this.popupMessage = error.message
          this.messageModal = 'unsuccess';
        }
      });
    }
  }

  handleOpenModal(modalType: string, clickedTip?: Tip): void {
    if (modalType === 'manage') {
      this.showManage = true;
    } else if (modalType === 'history') {
      this.showHistory = true;
    } else if (modalType === 'createAssess') {
      this.showCreateAssess = true;
    } else if (modalType === 'student info') {
      this.showStudentInfoForm = true;
    } else if (modalType === 'student info review') {
      this.showStudentInfoReview = true;
    } else if (modalType === 'student cases') {
      this.showStudentCases = true;
    }
    if (clickedTip) {
      this.currentTip = clickedTip
    }
    console.log('Current Tip in Dashboard:', this.currentTip);
  }

  handleCloseModal(modalType: string): void {
    if (modalType === 'manage') {
      this.showManage = false;
    } else if (modalType === 'history') {
      this.showHistory = false;
    } else if (modalType === 'createAssess') {
      this.showCreateAssess = false;
    } else if (modalType === 'student info') {
      this.showStudentInfoForm = false;
    } else if (modalType === 'student info review and continue') {
      this.showStudentInfoReview = false;
      this.showStudentCases = true;
    } else if (modalType === 'student info review') {
      this.showStudentInfoReview = false;
    } else if (modalType === 'student cases') {
      this.showStudentCases = false;
    }
  }

  handleSaveTip(message: {type: string, message: string}, actionType: string): void {
    if (actionType === 'manage') {
      this.handleCloseModal('manage');
    } 

    if (message.type === 'success') {
      this.loadAllTips();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'failed') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  handleSubmitStudentSSID(data: {student_identifier: string}): void {
    if (this.buildingId && this.regionId) {      
      this.loading = true;
      this.showStudentSSIDForm = false;
      const fullData = { ...data, building_id: this.buildingId };
      this.nSideAssessService.findStudentBySsid(this.regionId, fullData).subscribe({
        next: (response) => {
          this.handleCloseModal('createAssess');
          if (response.found === false) {
            this.loading = false;
            this.studentSSID = data.student_identifier;
            this.handleOpenModal('student info');
            return;
          } else if (response.found === true) {
            this.loading = false;
            this.currentStudent = response.student;
            this.handleOpenModal('student info review');
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching student info:', error);
          this.messageModal = 'unsuccess'
          this.popupMessage = error.message;
        }
      });
    } else {
      return
    }
  }

  handleCreateNewCaseForExistingStudent(data: {student_identifier: string}): void {
    if (this.buildingId && this.currentTip) {
      this.loading = true;
      this.showStudentCases = false;
      const formValues: IAssessCaseCreate = {
        student_identifier: data.student_identifier,
        building_id: Number(this.buildingId),
        detail_of_situation: 'TIP: ' + this.currentTip.tip_summary,
        tip_id: this.currentTip.id
      };
      this.nSideAssessService.createNewCaseForExistingStudent(formValues).subscribe({
        next: (response) => {
          this.loading = false;
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after creating a new Assess case for existing student.';
          this.handleCloseModal('student cases');
        },
        error: (error) => {
          this.loading = false;
          console.error('Error creating new case:', error);
          this.messageModal = 'unsuccess'
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleCreateStudent(data: IStudentCreate): void {
    if (this.currentTip && this.regionId) {
      this.loading = true;
      const payload: IStudentCreate = {
        ...data,
        detail_of_situation: 'TIP: ' + this.currentTip.tip_summary,
        tip_id: this.currentTip.id
      }
      console.log('Creating student with data:', payload);
      this.nSideAssessService.createStudent(this.regionId, payload).subscribe({
        next: (response) => {
          this.loading = false;
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after creating a new student and Assess case.';
          this.handleCloseModal('student info');
        },
        error: (error) => {
          this.loading = false;
          console.error('Error creating student:', error.message);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  toggleShowMore(index: number) {
    this.expandedTipIndex = this.expandedTipIndex === index ? null : index;
  }
}
